import { Link } from "react-router-dom";
import { Star, Calendar, Users, Sparkles, MapPin, Info, Megaphone } from "lucide-react";
import {
  CONFERENCE,
  BCS_LOGO,
  HERO_BG_URL,
  LUNCH_SPONSORS,
  ANNOUNCEMENTS,
} from "../data/conferenceData";

const quickActions = [
  { label: "Agenda", icon: Calendar, route: "/agenda", color: "var(--primary)", textColor: "white" },
  { label: "My Group", icon: Users, route: "/group", color: "var(--gold)", textColor: "white" },
  { label: "Sessions", icon: Sparkles, route: "/sessions", color: "var(--maroon)", textColor: "white" },
  { label: "Locations", icon: MapPin, route: "/locations", color: "var(--primary-dark)", textColor: "white" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO — full-width ImageBackground with overlay, bottom rounded corners (28px) */}
      <section
        className="relative w-full overflow-hidden"
        style={{
          minHeight: "360px",
          borderBottomLeftRadius: "28px",
          borderBottomRightRadius: "28px",
          backgroundImage: `url(${HERO_BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="home-hero"
      >
        <div
          className="relative w-full h-full flex flex-col justify-end"
          style={{
            minHeight: "360px",
            background: "rgba(9, 41, 54, 0.78)",
            padding: "32px 24px",
            borderBottomLeftRadius: "28px",
            borderBottomRightRadius: "28px",
          }}
        >
          {/* BCS logo: top-right, 72x72, white bg, 16px radius, 6px padding */}
          <div
            className="absolute"
            style={{
              top: "16px",
              right: "16px",
              width: "72px",
              height: "72px",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: "16px",
              padding: "6px",
            }}
            data-testid="bcs-logo"
          >
            <img
              src={BCS_LOGO}
              alt="BCS"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Gold pill with star — alignSelf flex-start, mb 16 */}
          <Link
            to="/success-starts-here"
            data-testid="home-pill-success"
            className="inline-flex items-center self-start"
            style={{
              backgroundColor: "var(--gold)",
              paddingLeft: "12px",
              paddingRight: "12px",
              paddingTop: "6px",
              paddingBottom: "6px",
              borderRadius: "999px",
              gap: "6px",
              marginBottom: "16px",
            }}
          >
            <Star size={14} fill="var(--primary-dark)" strokeWidth={0} />
            <span
              style={{
                color: "var(--primary-dark)",
                fontWeight: 800,
                fontSize: "12px",
                letterSpacing: "1px",
              }}
            >
              Success Starts Here
            </span>
          </Link>

          {/* Hero title — 44px weight 800, line-height 48, mb 8 */}
          <h1
            style={{
              color: "white",
              fontSize: "44px",
              fontWeight: 800,
              lineHeight: "48px",
              marginBottom: "8px",
            }}
          >
            Nurture.
            <br />
            Guide.
            <br />
            Empower.
          </h1>

          {/* Theme line — 16px white weight 700 mb 12 */}
          <p
            style={{
              color: "white",
              fontSize: "16px",
              fontWeight: 700,
              marginBottom: "12px",
            }}
          >
            {CONFERENCE.subTagline}
          </p>

          {/* Welcome — 15px white 92% line-height 22 */}
          <p
            style={{
              color: "rgba(255, 255, 255, 0.92)",
              fontSize: "15px",
              lineHeight: "22px",
            }}
          >
            {CONFERENCE.welcome}
          </p>
        </div>
      </section>

      {/* Lunch Sponsor card */}
      {LUNCH_SPONSORS.length > 0 && (
        <div style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "24px" }}>
          <div
            className="shadow-card"
            style={{
              backgroundColor: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "16px",
              padding: "20px",
              textAlign: "center",
            }}
            data-testid="home-lunch-sponsor"
          >
            <p
              style={{
                fontSize: "12px",
                fontWeight: 800,
                letterSpacing: "1.5px",
                color: "var(--gold)",
                textTransform: "uppercase",
                marginBottom: "10px",
              }}
            >
              Lunch Sponsor
            </p>
            <p
              style={{
                fontSize: "15px",
                fontWeight: 700,
                color: "var(--primary-dark)",
                marginBottom: "2px",
              }}
            >
              Lunch is sponsored by
            </p>
            <p
              style={{
                fontSize: "15px",
                fontWeight: 700,
                color: "var(--primary-dark)",
                marginBottom: "14px",
              }}
            >
              {LUNCH_SPONSORS[0].name}
            </p>
            <div
              data-testid="home-lunch-sponsor-logo"
              className="inline-block"
            >
              <img
                src={LUNCH_SPONSORS[0].logo}
                alt={LUNCH_SPONSORS[0].name}
                style={{
                  maxHeight: "70px",
                  maxWidth: "150px",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Quick Access — 2x2 grid with icons, 18 radius, minHeight 100, padding 16 */}
      <div style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "24px" }}>
        <p
          style={{
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "1.5px",
            color: "var(--primary)",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          Quick Access
        </p>
        <div className="flex flex-wrap" style={{ gap: "12px" }}>
          {quickActions.map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.label}
                to={a.route}
                data-testid={`quick-${a.label.toLowerCase().replace(" ", "-")}`}
                className="flex flex-col justify-between shadow-tile"
                style={{
                  width: "calc(50% - 6px)",
                  minHeight: "100px",
                  borderRadius: "18px",
                  padding: "16px",
                  backgroundColor: a.color,
                  color: a.textColor,
                }}
              >
                <Icon size={28} color={a.textColor} strokeWidth={1.8} />
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    marginTop: "8px",
                    color: a.textColor,
                  }}
                >
                  {a.label}
                </p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Info card — info icon + title + body */}
      <div style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "24px" }}>
        <div
          className="flex items-start"
          style={{
            backgroundColor: "var(--surface)",
            borderRadius: "16px",
            padding: "16px",
            border: "1px solid var(--border)",
          }}
          data-testid="home-event-info"
        >
          <Info size={22} color="var(--primary)" style={{ flexShrink: 0 }} />
          <div className="flex-1" style={{ marginLeft: "10px" }}>
            <p
              style={{
                fontWeight: 700,
                color: "var(--primary-dark)",
                fontSize: "14px",
                marginBottom: "4px",
              }}
            >
              District-Wide Professional Development
            </p>
            <p
              style={{
                fontWeight: 700,
                color: "var(--primary-dark)",
                fontSize: "14px",
                marginBottom: "4px",
              }}
            >
              {CONFERENCE.date}
            </p>
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "13px",
                lineHeight: "19px",
              }}
            >
              A full day of learning, collaboration, and discovery — designed
              for the educators who guide our 6-8 learners.
            </p>
          </div>
        </div>
      </div>

      {/* Announcements */}
      {ANNOUNCEMENTS.length > 0 && (
        <div
          style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "28px", paddingBottom: "16px" }}
          data-testid="home-announcements"
        >
          <div
            className="flex items-center"
            style={{ gap: "8px", marginBottom: "14px" }}
          >
            <Megaphone size={18} color="var(--primary-dark)" strokeWidth={2.2} />
            <p
              style={{
                fontSize: "13px",
                fontWeight: 800,
                letterSpacing: "1.5px",
                color: "var(--primary-dark)",
                textTransform: "uppercase",
              }}
            >
              Announcements
            </p>
          </div>

          {ANNOUNCEMENTS.map((a) => (
            <div
              key={a.id}
              className="shadow-card"
              style={{
                backgroundColor: "var(--surface)",
                border: "1px solid var(--border)",
                borderLeft: "4px solid var(--gold)",
                borderRadius: "12px",
                padding: "14px 16px",
                marginBottom: "12px",
              }}
              data-testid={`announcement-${a.id}`}
            >
              <p
                style={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "var(--primary-dark)",
                  marginBottom: "6px",
                  lineHeight: "20px",
                }}
              >
                {a.title}
              </p>
              <p
                style={{
                  fontSize: "13px",
                  color: "var(--text-muted)",
                  lineHeight: "20px",
                }}
              >
                {a.body}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
